/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ClickableTile } from 'carbon-components-react';
import urlJoin from 'proper-url-join';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { Instance as InstanceType } from '../../../../ts/instance';
import { getServerBaseUri } from '../../util/url';
import Instance from './Instance';

import './InstancesList.scss';

const messages = defineMessages({
  serviceInstancesTitle: {
    id: 'modules.ia.components.Instances.InstancesList.serviceInstancesTitle',
    defaultMessage: 'Service Instances:'
  },
  organizationsTitle: {
    id: 'modules.ia.components.Instances.InstancesList.organizationsTitle',
    defaultMessage: 'Organizations:'
  }
});

const makeState = (instances = []) => {
  const newState: any = {};

  newState.items = instances;
  newState.serviceInstances = {};
  newState.resourceInstances = [] as InstanceType[];

  newState.items.forEach(value => {
    if (value) {
      newState.resourceInstances.push(value);
    }
  });

  return newState;
};

export interface InstancesListProps {
  instances: InstanceType[];
}

const InstancesList: React.FC<InstancesListProps> = ({ instances }) => {
  const state = makeState(instances);
  return (
    <div className="InstancesList">
      {state.resourceInstances.length > 0 && (
        <div className="bx--row">
          <div className="bx--col">
            <div className="InstancesList__label InstancesList__instances-label">
              <FormattedMessage {...messages.serviceInstancesTitle} />
            </div>
          </div>
        </div>
      )}
      <div className="InstancesList__instance-list">
        <div className="bx--row">
          {state.resourceInstances.map((instance, j) => (
            <div
              key={j + 'rcinstance'}
              className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4 InstancesList__instance">
              <ClickableTile
                href={urlJoin(
                  getServerBaseUri(),
                  encodeURIComponent(instance.instance_id),
                  'home'
                )}
                target="_self"
                tabIndex={0}>
                <Instance className="InstancesList__card" instance={instance} />
              </ClickableTile>
            </div>
          ))}
        </div>
      </div>

      {Object.keys(state.serviceInstances).length > 0 && (
        <div className="bx--row">
          <div className="bx--col">
            <div className="InstancesList__label InstancesList__organization-label">
              <FormattedMessage {...messages.organizationsTitle} />
            </div>
          </div>
        </div>
      )}
      {Object.keys(state.serviceInstances).map((organization, i) => (
        <div key={i + 'org'}>
          <div className="bx--row">
            <div className="bx--col InstancesList__organization">
              {organization}
            </div>
          </div>
          <div className="bx--row InstancesList__instance-list">
            {state.serviceInstances[organization].map((instance, j) => (
              <div
                key={j + 'cfinstance'}
                className="bx--col-lg-8 bx--col-md-8 bx--col-sm-4 InstancesList__instance">
                <ClickableTile
                  href={urlJoin(
                    getServerBaseUri(),
                    encodeURIComponent(instance.instance_id),
                    'home'
                  )}
                  target="_self"
                  tabIndex={0}>
                  <Instance
                    className="InstancesList__card"
                    instance={instance}
                  />
                </ClickableTile>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default InstancesList;
